import React from 'react';
import { FinanceInfluencerPayment } from '@round/api';
import Table from '../../../../../ui/Table/Table';
import { Column } from 'react-table';
import Badge from '../../../../../ui/Badge/Badge';
import styles from './PastPaymentsTable.module.css';
import cn from 'classnames';
import { asMoney, formatDate } from '../../../../../utils/utits';
import useNonNullContext from '../../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../../contexts/OptionsContext';
import { PaymentMethodLabels } from '../../../../../utils/constants';
import { PopoverOnOverflow } from '@round/ui-kit';
import Skeleton from '../../../../../ui/Skeleton/Skeleton';
import range from 'lodash/range';

type PastPaymentsTableProps = {
    data: FinanceInfluencerPayment[];
    loading: boolean;
};

const PastPaymentsTable = ({ data, loading }: PastPaymentsTableProps) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const columns: Column<FinanceInfluencerPayment>[] = [
        {
            Header: 'ID',
            accessor: 'booking_id',
        },
        {
            Header: 'Campaign Name',
            accessor: 'campaign_name',
            Cell: ({ value }) => (
                <PopoverOnOverflow
                    popoverContent={<div className={styles.tooltip}>{value}</div>}
                    popoverOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 10] } }] }}
                >
                    {(setRef) => (
                        <Badge ref={setRef} className={cn(styles.badge, styles.info, styles.campaignName)}>
                            {value}
                        </Badge>
                    )}
                </PopoverOnOverflow>
            ),
        },
        {
            Header: 'Creator',
            accessor: 'influencer_name',
            Cell: ({ value }) => (
                <PopoverOnOverflow
                    popoverContent={<div className={styles.tooltip}>{value}</div>}
                    popoverOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 10] } }] }}
                >
                    {(setRef) => (
                        <Badge ref={setRef} className={cn(styles.badge, styles.info, styles.campaignName)}>
                            {value}
                        </Badge>
                    )}
                </PopoverOnOverflow>
            ),
        },
        {
            Header: 'Payment Date',
            accessor: 'created',
            Cell: ({ value }) => <>{formatDate(value, { month: 'numeric' })}</>,
        },
        {
            Header: 'Amount',
            accessor: 'amount',
            Cell: ({
                value,
                row: {
                    original: { currency_id },
                },
            }) => (
                <>
                    {asMoney(
                        value,
                        currencies.find((currency) => currency.id === currency_id)
                    )}
                </>
            ),
        },
        {
            Header: 'Payment Method',
            accessor: 'method',
            Cell: ({ value }) => <>{PaymentMethodLabels[value]}</>,
        },
        {
            Header: 'Paid By',
            accessor: 'paid_by',
            Cell: ({ value }) => <Badge className={cn(styles.badge, styles.info)}>{value}</Badge>,
        },
    ];

    return (
        <Table
            className={styles.table}
            renderBody={({ rows, prepareRow, columns }, renderRow) => (
                <tbody>
                    {rows.map((row) => {
                        prepareRow(row);
                        return renderRow(row);
                    })}
                    {loading &&
                        range(0, 10).map((i) => (
                            <tr key={i}>
                                {columns.map((column) => (
                                    <td key={column.id}>
                                        <Skeleton style={{ zIndex: 'unset' }} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            )}
            columns={columns}
            data={data}
        />
    );
};

export default PastPaymentsTable;
